import React, { useState } from 'react';
import { Multistep, SchemaFields } from 'informed';
import { Form } from '@tesla/informed-tds';
import { Button, FormLayout } from '@tesla/design-system-react';
import { string, func } from 'prop-types';
import { useIntl } from 'react-intl';
import style from './style.module.scss';
import { useTCLFormApi } from '../../hooks';
import {
  Country,
  FileUpload,
  HorizontalRadioGroup,
  LocalizedDate,
  ProfileLinks,
  Employers,
  TextAreaWithCounter,
} from '../Inputs';
import eeoInfo from '../Sections/EEOSection/eeo-v1.json';
import ApplicationStep from './ApplicationStep';

const JobApplicationForm = ({ jobId, onSubmit, applicationType }) => {
  const { formSchema, formType, multiStep, profileLinkFields, submitLabel } = useTCLFormApi();
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const intl = useIntl();
  const { locale } = intl;
  const asyncForEach = async (array, callback) => {
    // eslint-disable-next-line no-plusplus
    for (let index = 0; index < array.length; index++) {
      // eslint-disable-next-line no-await-in-loop
      await callback(array[index], index, array);
    }
  };

  const onFormSubmit = async ({ values }) => {
    setSubmitDisabled(true);
    const formValues = {
      formId: `tesla_careers_form__${applicationType}`,
      formType,
      formData: {
        fields: [
          {
            name: 'jobId',
            value: jobId,
          },
          {
            name: 'applicationType',
            value: applicationType,
          },
          {
            name: 'locale',
            value: locale.replace('_', '-'),
          },
        ],
      },
    };

    // Retrieve base64 encoded files uploads.
    await asyncForEach(Object.entries(values), async (entry) => {
      const name = entry[0];
      let value = entry[1];
      if (profileLinkFields.includes(name)) {
        // Exclude any empty values.
        if (value && value.length > 0) {
          value = value.filter((link) => link.type && link.type !== '' && link.url !== '');
        }
        value = value || [];
      }

      // Add EEO date and copy url dynamically.
      if (name === 'eeoDisabilityStatusName') {
        formValues.formData.fields.push({
          name: 'eeoDisabilityStatusDate',
          value: new Date().toISOString(),
        });
        formValues.formData.fields.push({
          name: 'eeoCopyUrl',
          value: eeoInfo.Url,
        });
      }

      // if form value name is in multistep we need to flatten it
      if (multiStep.find(({ id }) => id === name) && typeof value === 'object') {
        Object.keys(value).forEach((valueKey) => {
          // Add EEO date and copy url dynamically.
          if (valueKey === 'eeoDisabilityStatusName') {
            formValues.formData.fields.push({
              name: 'eeoDisabilityStatusDate',
              value: new Date().toISOString(),
            });
            formValues.formData.fields.push({
              name: 'eeoCopyUrl',
              value: eeoInfo.Url,
            });
            // As legal and EEO in same section, auto-fill legalAcknowledgmentName.
            if (applicationType === '003') {
              formValues.formData.fields.push({
                name: 'legalAcknowledgmentName',
                value: value[valueKey],
              });
            }
          }

          formValues.formData.fields.push({ name: valueKey, value: value[valueKey] });
        });
      } else {
        formValues.formData.fields.push({ name, value });
      }
    });

    onSubmit(formValues);
  };

  return (
    <Form
      className={`${style.JobApplicationForm} job-application-form-${applicationType}`}
      onSubmit={onFormSubmit}
      schema={formSchema}
      components={{
        country: Country,
        'localized-date': LocalizedDate,
        'file-upload': FileUpload,
        'radio-horizontal': HorizontalRadioGroup,
        'profile-links': ProfileLinks,
        employers: Employers,
        'textarea-counter': TextAreaWithCounter,
      }}
      errorMessage={{
        $required: 'Required',
      }}
    >
      <FormLayout>
        {multiStep.length > 0 ? (
          <Multistep>
            {multiStep.map((info) => (
              <ApplicationStep {...info} key={info.id} />
            ))}
          </Multistep>
        ) : (
          <>
            <SchemaFields />
            <Button type="submit" data-action-type="submit" disabled={submitDisabled}>
              {submitLabel}
            </Button>
          </>
        )}
      </FormLayout>
    </Form>
  );
};

JobApplicationForm.propTypes = {
  jobId: string.isRequired,
  onSubmit: func,
  applicationType: string.isRequired,
};

JobApplicationForm.defaultProps = {
  onSubmit: () => {},
};

export default JobApplicationForm;
